'use client';

import { Star, Quote, Check } from 'lucide-react';
import { getAllVerifiedReviews } from '@/lib/reviews';

export default function Testimonials() {
  const reviews = getAllVerifiedReviews().slice(0, 6);

  return (
    <section
      id="temoignages"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Témoignages Clients</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            <span className="text-[#F4E8C1]">Ce que disent</span>{' '}
            <span className="text-gold-gradient">nos confidents.</span>
          </h2>
          <p className="max-w-2xl mx-auto text-[#F4E8C1]/65 font-light leading-relaxed">
            Plus de 10 000 clients à travers le monde ont confié leur allure à
            MATAR STYLE CUIR. Voici quelques-unes de leurs histoires véritables.
          </p>
        </div>

        {/* Aggregate stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
          {[
            { value: '4,9/5', label: 'Note moyenne', sub: 'sur 1 247 avis' },
            { value: '10 000+', label: 'Clients confidents', sub: 'dans 42 pays' },
            { value: '98%', label: 'Recommandent', sub: 'la maison' },
            { value: '4,2 j', label: 'Délai moyen', sub: 'confirmation à livraison' },
          ].map((s) => (
            <div
              key={s.label}
              className="text-center bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-5"
            >
              <div className="font-display text-2xl sm:text-3xl font-bold text-gold-gradient">
                {s.value}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/70 mt-2 font-semibold">
                {s.label}
              </div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#F4E8C1]/45 mt-1">
                {s.sub}
              </div>
            </div>
          ))}
        </div>

        {/* Reviews grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((r) => (
            <article
              key={r.id}
              className="relative bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-6 hover:border-[#D4AF37]/45 transition-colors duration-400 group"
            >
              {/* Quote icon */}
              <Quote size={28} className="text-[#D4AF37]/40 mb-3" />

              {/* Rating */}
              <div className="flex items-center gap-0.5 mb-3">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    size={12}
                    className={s <= r.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#D4AF37]/25'}
                  />
                ))}
                <span className="ml-2 text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/85 flex items-center gap-1">
                  <Check size={10} />
                  Achat vérifié
                </span>
              </div>

              {/* Title */}
              <h3 className="font-display text-base font-semibold text-[#F4E8C1] mb-2 tracking-wide">
                {r.title}
              </h3>

              {/* Text */}
              <p className="text-xs text-[#F4E8C1]/70 leading-relaxed font-light mb-5">
                {r.text}
              </p>

              {/* Author */}
              <div className="pt-4 border-t border-[#D4AF37]/15 flex items-center justify-between">
                <div>
                  <div className="font-display text-sm font-semibold text-[#F4E8C1]">
                    {r.author}
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#F4E8C1]/55 mt-0.5">
                    {r.location}
                  </div>
                </div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/70">
                  {new Date(r.date).toLocaleDateString('fr-BE', {
                    year: 'numeric',
                    month: 'short',
                  })}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Final CTA */}
        <div className="mt-16 text-center max-w-2xl mx-auto">
          <p className="font-serif italic text-lg sm:text-xl text-[#F4E8C1]/85 leading-relaxed mb-5">
            « La meilleure publicité d'une maison de cuir, ce sont les pièces
            que l'on croise vingt ans après, portées par la fille de celle qui
            les a achetées. »
          </p>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-7">
            — S.E. MATAR, Maître Artisan
          </p>
          <a
            href="#boutique-shop"
            className="inline-flex items-center justify-center gap-3 px-9 py-4 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.18em] text-xs hover:glow-gold-strong transition-all duration-500"
          >
            Rejoindre nos clients confidents
          </a>
        </div>
      </div>
    </section>
  );
}
